"use client"
import Heading from "@/app/(site)/_components/Atoms/Heading"
import PortableTextRenderer from "@/app/(site)/_components/Atoms/PortableTextRenderer/PortableTextRenderer"
import StoryText from "@/app/(site)/_components/Atoms/Storytext"
import Text from "@/app/(site)/_components/Atoms/Text"
import ButtonLink from "@/app/(site)/_components/Molecules/ButtonLink"
import PageHeadTrimMenu from "@/app/(site)/_components/Organisms/PageHeadTrim/PageHeadTrimMenu"
import ScrollableRow from "@/app/(site)/_components/Organisms/PageHeadTrim/ScrollableRow"
import { MenuItemTypes } from "@/app/(site)/_mockdata/menuItems"
import { isClient } from "@/lib/env-utils/env-utils"
import { breakWordsAtSlash, numberOrDefault } from "@/lib/utils"
import { t } from "@/translations"
import clsx from "clsx"
import { useSearchParams } from "next/navigation"
import { useState } from "react"
import { PortableTextBlock } from "sanity"

interface PageHeadTrim {
    heading?: string
    text?: string | PortableTextBlock[]
    buttons?: MenuItemTypes[]
    menu?: {
        title: string
        items: { title: string; uri: string; isActive?: boolean }[]
    }
    showMenu?: boolean
    className?: string
    langCode: string
}

export default function PageHeadTrim({
    heading,
    text,
    buttons,
    menu,
    showMenu,
    className,
    langCode
}: PageHeadTrim) {
    const hasNeitherMenuNorButtons = (!menu?.items || !showMenu) && !buttons?.length
    const page = Number(useSearchParams().get("page")) || 1

    const historyInitialPage = isClient ? history?.state?.initialPage : undefined
    const [initialPagination] = useState(numberOrDefault(historyInitialPage, page))
    console.log("initialPagination", initialPagination, historyInitialPage)
    const showPagination = initialPagination !== 1
    const isPressroom = heading === "Pressroom"

    return (
        <section className={clsx(className, "px-16 pt-16 md:px-24 md:pt-24")}>
            <div className="grid grid-cols-2 gap-24 gap-y-32 md:gap-y-48">
                <div
                    className={`col-span-2 mt-32 box-content break-normal last:mb-24 md:col-span-1 md:mt-0 ${hasNeitherMenuNorButtons ? "md:col-span-2" : ""}`}
                >
                    <Heading
                        type="displayTrim"
                        className="mb-64 !text-30 sm:text-38 md:!text-64"
                    >
                        {breakWordsAtSlash(heading ?? "")}
                        {showPagination && (
                            <Text
                                size="small"
                                weight="regular"
                                className="mt-8 block text-main-dark-grey"
                                asChild
                            >
                                <span>{`${t(langCode, "category_page.current_page", { currentPage: page })}`}</span>
                            </Text>
                        )}
                    </Heading>
                    {isPressroom && (
                        <div className="mb-64 md:mb-144">
                            <PortableTextRenderer
                                content={text as PortableTextBlock[]}
                            ></PortableTextRenderer>
                        </div>
                    )}
                </div>
                {/* Removed code below because of https://app.asana.com/0/1206016380051535/1207478874403654/f */}
                {/* The following div puts text below heading on desktop */}
                {/* {hasNeitherMenuNorButtons ? <div className="hidden md:block" /> : null} */}
                {buttons && buttons?.length > 0 && !isPressroom && (
                    <div className="col-span-2 last:mb-16 md:col-span-1 md:row-span-2">
                        <div className="md:hidden">
                            <ScrollableRow>
                                {buttons.map((link, index) => {
                                    if (link.isNavigationContainer) return null
                                    return (
                                        <ButtonLink
                                            key={link?.uri ?? `button-${index}`}
                                            href={link?.uri}
                                            color="black"
                                            size="small"
                                            variant="secondary"
                                            className="whitespace-nowrap !text-12"
                                        >
                                            {link?.title}
                                        </ButtonLink>
                                    )
                                })}
                            </ScrollableRow>
                        </div>
                        <div className="hidden flex-wrap gap-8 md:flex">
                            {buttons.map((link, index) => {
                                if (link.isNavigationContainer) return null
                                return (
                                    <ButtonLink
                                        key={link?.uri ?? `button-${index}`}
                                        href={link?.uri}
                                        color="black"
                                        size="small"
                                        variant="secondary"
                                        className="whitespace-nowrap !text-12"
                                    >
                                        {link?.title}
                                    </ButtonLink>
                                )
                            })}
                        </div>
                    </div>
                )}
                {menu?.items && showMenu && (
                    <div className="order-3 col-span-2 mb-48 flex gap-8 md:order-2 md:col-span-1 md:row-span-2 md:mb-0">
                        <div className="ml-auto min-w-full md:min-w-[360px]">
                            <PageHeadTrimMenu menu={menu} />
                        </div>
                    </div>
                )}
                {text && !isPressroom && !showPagination && (
                    <div className="order-2 col-span-2 mb-64 md:order-3 md:col-span-1 md:mb-144">
                        {typeof text === "string" ? (
                            <StoryText size="size3">{text}</StoryText>
                        ) : (
                            <PortableTextRenderer
                                content={text as PortableTextBlock[]}
                            ></PortableTextRenderer>
                        )}
                    </div>
                )}
            </div>
        </section>
    )
}
